import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import { format, sub, add } from 'date-fns';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { isBeforeNoon } from '../../utils/date.js';
import { InputInput, getInputStyles } from '../Input/InputInput.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["dateFormat", "disabled", "locale", "onBlur", "onChange", "value"];
var StyledInputInput = styled(InputInput).withConfig({
  displayName: "DateSpinButton__StyledInputInput",
  componentId: "sc-15uc92k-0"
})(["&&{user-select:none;cursor:", ";}&&:focus{border-radius:2px;background:", ";color:", ";}&&::selection{background:transparent;}"], function (_ref) {
  var disabled = _ref.disabled;
  return disabled ? "not-allowed" : "pointer";
}, function (props) {
  return props.theme.CTAColors.primary;
}, function (props) {
  return props.theme.neutralColors.white;
});
var StyledGroup = styled.div.withConfig({
  displayName: "DateSpinButton__StyledGroup",
  componentId: "sc-15uc92k-1"
})(["&&{display:inline-flex;outline:0;", ";}"], function (props) {
  return props.empty && getInputStyles(props)["::placeholder"];
});
function getDurationFromPart(part) {
  if (part.match(/(y|Y)+/)) return {
    years: 1
  };
  if (part.match(/M+/)) return {
    months: 1
  };
  if (part.match(/(d)+o?/)) return {
    days: 1
  };
  if (part.match(/(h|H)+/)) return {
    hours: 1
  };
  if (part.match(/mm/)) return {
    minutes: 1
  };
  if (part.match(/ss/)) return {
    seconds: 1
  };
}

function isMeridianPart(part) {
  return part.match(/a+/);
}

function switchMeridian(date) {
  if (isBeforeNoon(date)) return add(date, {
    hours: 12
  });
  return sub(date, {
    hours: 12
  });
}

var datePartRegex = /[a-zA-Z]+/;
var DateSpinButton = /*#__PURE__*/React.forwardRef(function DateSpinButton2(props, ref) {
  var dateFormat = props.dateFormat,
      disabled = props.disabled,
      locale = props.locale,
      onBlur = props.onBlur,
      onChange = props.onChange,
      value = props.value,
      rest = _objectWithoutProperties(props, _excluded);

  var formatParts = React.useMemo(function () {
    return dateFormat.split("").reduce(function (acc, char) {
      var lastIndex = acc.length - 1;
      if (acc[lastIndex] && acc[lastIndex].match(datePartRegex) && char.match(datePartRegex)) acc[lastIndex] = acc[lastIndex] + char;else acc.push(char);
      return acc;
    }, []);
  }, [dateFormat]);
  return /*#__PURE__*/jsx(StyledGroup, _objectSpread(_objectSpread({
    role: "group",
    ref: ref,
    empty: !value,
    onBlur: onBlur
  }, rest), {}, {
    children: formatParts.map(function (part, i) {
      if (part.match(datePartRegex)) {
        var handleKeyDown = function handleKeyDown(e) {
          if ([KEYS.ARROW_DOWN, KEYS.ARROW_UP].includes(e.key)) {
            e.preventDefault();
            if (!onChange) return;
            var operate = e.key === KEYS.ARROW_DOWN ? sub : add;
            var duration = getDurationFromPart(part);
            var startValue = value || new Date();
            if (duration) return onChange(operate(startValue, duration));
            if (isMeridianPart(part)) return onChange(switchMeridian(startValue));
          }
        };

        var formattedValue = value ? format(value, part, {
          locale: locale
        }) : "";
        return /*#__PURE__*/jsx(StyledInputInput, {
          "aria-label": part,
          autosize: true,
          disabled: disabled,
          onKeyDown: handleKeyDown,
          placeholder: part,
          readOnly: true,
          role: "spinbutton",
          value: formattedValue
        }, i);
      } else if (part === " ") {
        return /*#__PURE__*/jsx("span", {
          children: "\xA0"
        }, i);
      } else {
        return /*#__PURE__*/jsx("span", {
          children: part
        }, i);
      }
    })
  }));
});
DateSpinButton.propTypes = {
  "dateFormat": _PropTypes.string.isRequired,
  "disabled": _PropTypes.bool,
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "onBlur": _PropTypes.func,
  "onChange": _PropTypes.func,
  "value": _PropTypes.instanceOf(Date)
};
DateSpinButton.displayName = "DateSpinButton";

export { DateSpinButton, getDurationFromPart };
